/**
 * Composable per Tasks autocomplete con loading e error handling centralizzati
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { BaseAutocompleteOptions } from '@/lib/autocompletes';
import type { TaskAutocompleteResult } from '@/lib/models/task';
import { getTasksAutocomplete } from '@/lib/autocompletes/tasks';

export function useTasksAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const tasks = ref<TaskAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	// Usa withLoading iniettato dal parent, fallback su istanza locale se non fornito
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchTasks(options: BaseAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getTasksAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					tasks.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useTasksAutocomplete.searchTasks:', err);
				error('Errore durante il caricamento dei Task! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					tasks.value = [];
				}
			}
		}, 'searchTasks');
	}

	function clearTasks(): void {
		tasks.value = [];
	}

	return {
		tasks: readonly(tasks),
		searchTasks,
		clearTasks,
	};
}
